import { Box, Paper, Typography } from "@mui/material";
import React from "react";
import { DotLegalCheckbox, DotLegalInformationTooltip } from "@dotlegal/dotlegal-ui-components";
import { useTranslation } from "../../../localization/useTranslation";
import { DistributionArea, SystemDetailData } from "../SystemsStep.types";
import { useSystemSelectorStyles } from "./SystemSelector.styles";
import { useUrlProvider } from "../../../useUrlProvider";
import DotLegalSelectWithButton from "../../../common/components/dotLegalSelectWithButton/DotLegalSelectWithButton";
import { useSystemSelectorHook } from "./SystemSelector.hooks";
import AddSystemDialog from "../../../system/addSystemDialog/AddSystemDialog";
import { useUserContext } from "../../../auth/userContextProvider/UserContextProvider";
import { GetSystemsPlanLimit } from "../../../auth/planProvider/PlanProvider.hooks.ts";
import { SelectableItem } from "../../ProcessingActivity.types";

export interface ISystemSelector {
    onSystemChange: (systemId: string | undefined) => void;
    selectableSystems?: Array<SelectableItem>;
    systemDetail?: SystemDetailData;
    chosenSystemId?: string;
    isLoading: boolean;
    readOnly?: boolean;
    onSystemCreated: (id: string) => void;
    onGenerateSharingsUpdate: (generateSharings: boolean) => void;
    generateSharings: boolean;
}

function SystemSelector(props: ISystemSelector) {
    const { getSystemOverviewUrl } = useUrlProvider();
    const { translateString } = useTranslation();
    const styles = useSystemSelectorStyles();
    const { permissions } = useUserContext();
    const { setShowAddDialog, showAddDialog, searchValue, setSearchValue } = useSystemSelectorHook();
    const { data: systemsPlanLimitData } = GetSystemsPlanLimit();

    return (
        <React.Fragment>
            <Box sx={styles.systemContainer}>
                <DotLegalSelectWithButton
                    link={
                        props.chosenSystemId && permissions.systemPermissions?.read
                            ? { tooltip: translateString("goToSystem"), href: getSystemOverviewUrl(props.chosenSystemId) }
                            : undefined
                    }
                    label={translateString("system")}
                    placeholder={translateString("system")}
                    options={
                        props.selectableSystems
                            ? props.selectableSystems.map((obj) => {
                                  return {
                                      id: obj.id,
                                      name: obj.name,
                                  };
                              })
                            : undefined
                    }
                    onChange={(sys) => props.onSystemChange(sys ? sys : undefined)}
                    isLoading={props.isLoading}
                    selectedItem={props.chosenSystemId}
                    toolTipText={translateString("systemHelperText")}
                    disableClearable
                    disabled={props.readOnly}
                    noOptionsLabel={translateString("noOptions")}
                    onButtonClick={() => setShowAddDialog(true)}
                    hasPermission={
                        permissions.systemPermissions.create && !props.readOnly && systemsPlanLimitData !== undefined && !systemsPlanLimitData.limitReached
                    }
                    buttonLabel={translateString("createNew")}
                    searchValue={searchValue}
                    onSearchValueChange={setSearchValue}
                />
            </Box>
            {props.systemDetail && (
                <Paper elevation={0} sx={styles.paper}>
                    <Box sx={{ display: "flex", flexDirection: "column" }}>
                        <Box
                            sx={[
                                styles.container,
                                {
                                    marginBottom:
                                        props.systemDetail.generateSharings ||
                                        props.systemDetail.dataProcessors.length === 0 ||
                                        !permissions.processingActivitySystemPermissions.permissions.edit
                                            ? 0
                                            : -2.5,
                                },
                            ]}
                        >
                            <Box sx={styles.flex}>
                                <Box sx={styles.toolTip}>
                                    <DotLegalInformationTooltip text={translateString("systemDataProcessorHelperText")} />
                                </Box>
                                <Typography sx={styles.primaryText}> {translateString("dataProcessors")}:</Typography>&nbsp;
                            </Box>
                            <Typography sx={styles.secondaryText}>{props.systemDetail.dataProcessors.join(", ")}</Typography>
                        </Box>
                        {!props.systemDetail.generateSharings &&
                            props.systemDetail.dataProcessors.length > 0 &&
                            permissions.processingActivitySystemPermissions.permissions.edit && (
                                <DotLegalCheckbox
                                    onChange={props.onGenerateSharingsUpdate}
                                    margin={{ marginLeft: 2.8 }}
                                    checked={props.generateSharings}
                                    label={translateString("createTransfer")}
                                    tooltip={translateString("createTransferToolTip")}
                                />
                            )}
                    </Box>
                    <Box sx={styles.container}>
                        <Box sx={styles.flex}>
                            <Box sx={styles.toolTip}>
                                <DotLegalInformationTooltip text={translateString("securitymeasuresHelperText")} />
                            </Box>
                            <Typography sx={styles.primaryText}> {translateString("securityMeasures")}:</Typography>&nbsp;
                        </Box>
                        <Typography sx={styles.secondaryText}>
                            {props.systemDetail.securityMeasureNames ? props.systemDetail.securityMeasureNames.join(", ") : ""}
                        </Typography>
                    </Box>
                    <Box sx={styles.container}>
                        <Box sx={styles.flex}>
                            <Box sx={styles.toolTip}>
                                <DotLegalInformationTooltip text={translateString("distributionAreaHelperText")} />
                            </Box>
                            <Typography sx={styles.primaryText}> {translateString("distributionArea")}:</Typography>&nbsp;
                        </Box>
                        <Typography sx={styles.secondaryText}>
                            {translateString(DistributionArea[Number(props.systemDetail.distributionArea)].toLocaleString().toLowerCase())}
                        </Typography>
                    </Box>
                    <Box sx={styles.container}>
                        <Box sx={styles.flex}>
                            <Box sx={styles.toolTip}>
                                <DotLegalInformationTooltip text={translateString("responsibleHelperText")} />
                            </Box>
                            <Typography sx={styles.primaryText}> {translateString("responsible")}:</Typography>&nbsp;
                        </Box>
                        <Typography sx={styles.secondaryText}>{props.systemDetail.responsibleName}</Typography>
                    </Box>
                </Paper>
            )}

            {showAddDialog && (
                <AddSystemDialog
                    showDialog
                    onHideDialog={() => setShowAddDialog(false)}
                    searchedName={searchValue}
                    onSystemCreated={(id) => {
                        setShowAddDialog(true);
                        props.onSystemCreated(id);
                    }}
                />
            )}
        </React.Fragment>
    );
}

export default SystemSelector;
